import { useQueries } from 'react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from 'services/api';
import { Endpoints } from 'utils/endpoints';
import { useCurrentUser } from 'providers/UserProvider';
import { useMemo } from 'react';

export const featureFlagConfig = (userId: number, flag: string) =>
  ({
    queryKey: [flag, userId],
    queryFn: () => apiClient.get<Record<string, boolean>>(Endpoints.getUserFeatureFlagContext(userId, flag)),
    staleTime: Infinity,
  } satisfies UseQueryOptions);

const useFeatureFlagQueries = (flags: string[], userId: number) =>
  useQueries(
    flags.map((flag) => ({
      ...featureFlagConfig(userId, flag),
      enabled: !!userId,
    }))
  );

export const useFeatureFlagWithUserId = (flags: string[], userId: number): Record<string, boolean | undefined> => {
  const queryResults = useFeatureFlagQueries(flags, userId);

  const featureFlags = useMemo(() => {
    let ab: Record<string, boolean> = {};
    queryResults.forEach(({ data }) => {
      if (data) {
        ab = { ...ab, ...data };
      }
    });
    return ab;
  }, [queryResults]);

  return featureFlags;
};

const useFeatureFlag = (flag: string[]): Record<string, boolean | undefined> => {
  const { data } = useCurrentUser();
  const userId = data.id;

  return useFeatureFlagWithUserId(flag, userId);
};

export interface FeatureFlagStatus {
  flags: Record<string, boolean | undefined>;
  isError: boolean;
  isLoading: boolean;
}

export const useFeatureFlagStatus = (flags: string[]): FeatureFlagStatus => {
  const { data } = useCurrentUser();
  const userId = data.id;
  const queryResults = useFeatureFlagQueries(flags, userId);

  return useMemo(() => {
    let ab: Record<string, boolean> = {};
    let isError = false;
    let isLoading = false;
    queryResults.forEach(({ data, isError: queryIsError, isLoading: queryIsLoading }) => {
      if (data) ab = { ...ab, ...data };
      if (queryIsError) isError = true;
      if (queryIsLoading) isLoading = true;
    });
    return { flags: ab, isError, isLoading };
  }, [queryResults]);
};

export default useFeatureFlag;
